import { useEffect, useMemo, useState } from "react";
import { api, euro } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileTextIcon as FileText, TrendUpIcon as TrendUp, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ClockIcon as Clock, WarningCircleIcon as WarningCircle, CalendarBlankIcon as CalendarBlank } from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, tone = "text-primary", testid }) => (
  <Card className="rounded-sm border-border shadow-none p-5" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Icon size={20} weight="bold" className={tone} /></div>
      <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-xl font-heading font-bold tabular-nums">{value}</div></div>
    </div>
  </Card>
);

const GroupTable = ({ title, rows, testid }) => (
  <Card className="rounded-sm border-border shadow-none" data-testid={testid}>
    <div className="p-4 border-b border-border"><h3 className="font-heading font-semibold text-sm">{title}</h3></div>
    <Table>
      <TableHeader><TableRow><TableHead>Voce</TableHead><TableHead className="text-right">Preventivi</TableHead><TableHead className="text-right">Accettati</TableHead><TableHead className="text-right">Valore</TableHead></TableRow></TableHeader>
      <TableBody>
        {(!rows || rows.length === 0) ? <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Nessun dato.</TableCell></TableRow>
          : rows.map((r) => <TableRow key={r.chiave}><TableCell className="font-medium">{r.chiave}</TableCell><TableCell className="text-right tabular-nums">{r.numero}</TableCell><TableCell className="text-right tabular-nums">{r.accettati}</TableCell><TableCell className="text-right tabular-nums">{euro(r.valore)}</TableCell></TableRow>)}
      </TableBody>
    </Table>
  </Card>
);

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const pad = (x) => String(x).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m = 1..12

export default function PreventiviStats() {
  const nowY = new Date().getFullYear();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granularita, setGranularita] = useState("anno"); // "anno" | "mese" (min mensile)
  const [anno, setAnno] = useState(nowY);
  const [mese, setMese] = useState(new Date().getMonth() + 1); // 1..12

  const anni = useMemo(() => Array.from({ length: 6 }, (_, i) => nowY - i), [nowY]);

  const range = useMemo(() => {
    if (granularita === "mese") {
      return { data_da: `${anno}-${pad(mese)}-01`, data_a: `${anno}-${pad(mese)}-${pad(lastDay(anno, mese))}` };
    }
    return { data_da: `${anno}-01-01`, data_a: `${anno}-12-31` };
  }, [granularita, anno, mese]);

  useEffect(() => {
    setLoading(true);
    api.get("/crm/preventivi/stats", { params: range })
      .then(({ data }) => setS(data))
      .finally(() => setLoading(false));
  }, [range]);

  const periodoLabel = granularita === "mese" ? `${MESI[mese - 1]} ${anno}` : `Anno ${anno}`;

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Statistiche commerciali" subtitle="Pipeline preventivi e conversione" />

        <Card className="rounded-sm border-border shadow-none p-4 mb-6" data-testid="periodo-selector">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 text-muted-foreground mr-1">
              <CalendarBlank size={18} weight="bold" className="text-primary" />
              <span className="text-xs uppercase tracking-wider font-semibold">Periodo</span>
            </div>
            <div>
              <div className="flex items-center gap-1 bg-secondary/60 rounded-sm p-0.5" data-testid="granularita-selector">
                {[{ v: "anno", l: "Anno" }, { v: "mese", l: "Mese" }].map((o) => (
                  <button key={o.v} onClick={() => setGranularita(o.v)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${granularita === o.v ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`granularita-${o.v}`}>{o.l}</button>
                ))}
              </div>
            </div>
            {granularita === "mese" && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Mese</div>
                <Select value={String(mese)} onValueChange={(v) => setMese(Number(v))}>
                  <SelectTrigger className="h-9 w-36 rounded-sm" data-testid="mese-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)} data-testid={`mese-opt-${i + 1}`}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Anno</div>
              <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
                <SelectTrigger className="h-9 w-28 rounded-sm" data-testid="anno-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anni.map((y) => <SelectItem key={y} value={String(y)} data-testid={`anno-opt-${y}`}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-xs text-muted-foreground" data-testid="periodo-label">
              Visualizzazione: <span className="font-semibold text-foreground">{periodoLabel}</span>
            </div>
          </div>
        </Card>

        {loading || !s ? (
          <div className="p-12 text-muted-foreground" data-testid="stats-loading">Caricamento…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat icon={FileText} label="Preventivi totali" value={s.totali} testid="stat-totali" />
              <Stat icon={TrendUp} label="Valore totale" value={euro(s.valore_totale)} testid="stat-valore-totale" />
              <Stat icon={Clock} label="Valore aperti" value={euro(s.valore_aperti)} tone="text-blue-600" testid="stat-valore-aperti" />
              <Stat icon={CheckCircle} label="Valore accettati" value={euro(s.valore_accettati)} tone="text-green-600" testid="stat-valore-accettati" />
              <Stat icon={XCircle} label="Valore persi" value={euro(s.valore_persi)} tone="text-rose-600" testid="stat-valore-persi" />
              <Stat icon={TrendUp} label="Tasso conversione" value={`${s.tasso_conversione}%`} tone="text-emerald-600" testid="stat-conversione" />
              <Stat icon={WarningCircle} label="In scadenza 30gg" value={s.in_scadenza_30} tone="text-amber-600" testid="stat-scadenza30" />
              <Stat icon={WarningCircle} label="Scaduti non chiusi" value={s.scaduti_non_chiusi} tone="text-rose-600" testid="stat-scaduti" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <GroupTable title="Per categoria" rows={s.per_categoria} testid="stat-per-categoria" />
              <GroupTable title="Per responsabile" rows={s.per_responsabile} testid="stat-per-responsabile" />
              <GroupTable title="Per origine" rows={s.per_origine} testid="stat-per-origine" />
            </div>
          </>
        )}
      </div>
    </InternalLayout>
  );
}
